import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box, Button, Grid, Typography } from '@mui/material'
import { GridSortModel } from '@mui/x-data-grid'

import { CopyDialog } from '@pages/ConfigWindows/components/CopyDialog'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EntityOrFormList } from '@components/EntityOrFormList'
import { PageTitle } from '@components/PageTitle'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import {
  useCopyWindowMutation,
  useDeleteWindowMutation,
  useFetchAllWindowsQuery,
} from '@redux/api/windows.api'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { useDebounce } from '@hooks'
import { getSortParamRequest } from '@helpers'
import {
  CONFIG_WINDOWS_COLUMNS,
  ENTITY_CONFIG_TYPE,
  GENERATOR_INPUT_TYPE,
  ROUTES,
  SEARCH_PATTERN_DELAY,
  VISIBLE_HIDDEN,
} from '@constants'
import { POSTCopyWindowParams } from '@types'

export const ConfigWindows = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const isAllWindows = matchPath(ROUTES.CONFIG_WINDOWS, pathname)

  const methods = useForm<{ searchPattern: string }>({ defaultValues: { searchPattern: '' } })

  const { watch } = methods

  const watchSearchPattern = watch('searchPattern')
  const debouncedSearchPattern = useDebounce(watchSearchPattern, SEARCH_PATTERN_DELAY)
  const [copyWindowData, setCopyWindowData] = useState<POSTCopyWindowParams | null>(null)
  const [currentSort, setCurrentSort] = useState<GridSortModel>([])

  const {
    data: windows,
    isLoading,
    isFetching,
  } = useFetchAllWindowsQuery(
    {
      sort: getSortParamRequest(currentSort) || undefined,
      searchPattern: debouncedSearchPattern ? debouncedSearchPattern : undefined,
    },
    { refetchOnMountOrArgChange: true }
  )

  const loading = isLoading || isFetching

  const [copyWindow, { isLoading: isLoadingCopyWindow }] = useCopyWindowMutation()
  const [deleteWindow] = useDeleteWindowMutation()

  const handleCreate = () => navigate(ROUTES.CONFIG_WINDOWS_CREATE)

  const handleEdit = (_code: string, id: string | number) =>
    navigate(`${ROUTES.CONFIG_WINDOWS}/edit/${id}`)

  const handleCopy = (window: POSTCopyWindowParams) => {
    const { id, title } = window
    setCopyWindowData({ id, title })
  }
  const handleCloseCopyDialog = () => setCopyWindowData(null)

  const handleCopyWindow = (data: POSTCopyWindowParams) => {
    copyWindow(data)
      .unwrap()
      .then(res => {
        if (res) {
          setCopyWindowData(null)
          navigate(`${ROUTES.CONFIG_WINDOWS_EDIT}/${res.id}`)
        }
      })
  }

  const handleDeleteWindow = (id: number | string) => {
    deleteWindow(id)
  }

  const handleSetSorting = (value: GridSortModel | null) => {
    setCurrentSort(value ? value : [])
  }

  return (
    <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.WINDOWS}>
      <Outlet />
      {copyWindowData && (
        <CopyDialog
          copyData={copyWindowData}
          dialogTitle={t('window.title.copy')}
          loading={isLoadingCopyWindow}
          models={windows?.map(window => window.internalId) ?? []}
          onClose={handleCloseCopyDialog}
          onCopy={handleCopyWindow}
        />
      )}
      <Box
        sx={{
          ...(!isAllWindows && VISIBLE_HIDDEN),
          overflow: 'hidden',
        }}
      >
        <PageContentLayout>
          <PageTitle
            end={
              <Box sx={{ py: 0.5 }}>
                <EditConfigMenu />
              </Box>
            }
            start={
              <>
                <Typography display={'inline'} sx={{ mr: 1, ml: 1 }} variant={'h6'}>
                  {t('pageTitle.windows')}
                </Typography>
              </>
            }
          />
          <ExportAllCheckbox />
          <Grid container alignItems='center' flexWrap='nowrap' gap={1}>
            <Grid item>
              <Button sx={{ mb: 1, width: 150 }} variant={'contained'} onClick={handleCreate}>
                {t(`configWindow.add`)}
              </Button>
            </Grid>
            <Grid container item flexGrow={1} flexWrap='nowrap' spacing={2}>
              <FormProvider {...methods}>
                <Grid item flexGrow={1}>
                  <FormInput
                    inputType={GENERATOR_INPUT_TYPE.INPUT}
                    name='searchPattern'
                    placeholder={t('configViews.allTab.search')}
                    size='small'
                  />
                </Grid>
              </FormProvider>
            </Grid>
          </Grid>
          <EntityOrFormList
            columns={CONFIG_WINDOWS_COLUMNS}
            currentSort={currentSort}
            loading={loading}
            rows={windows || []}
            onCopy={handleCopy}
            onDelete={handleDeleteWindow}
            onEdit={handleEdit}
            onSetSorting={handleSetSorting}
          />
        </PageContentLayout>
      </Box>
    </EditConfigProvider>
  )
}
